import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, Button, Stack, Chip, Grid, Skeleton,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import ParkIcon from '@mui/icons-material/Park';
import { generateClient } from 'aws-amplify/api';

import { listActiveStayPackages } from '../../graphql/farmStayQueries';
import { formatINR } from '../../utils/farmStayPricing';
import { surface, brand, ink } from '../../theme/tokens';

const client = generateClient();

/** Mustard is a surface, never text on cream: dark ink sits on top of it. */
const MUSTARD_BUTTON_SX = {
  bgcolor: brand.mustard,
  color: ink.onMustard,
  fontWeight: 700,
  '&:hover': { bgcolor: darken(brand.mustard, 0.12) },
};

const HIGHLIGHTS = [
  { icon: <RestaurantMenuIcon />, title: 'Bengali home cooking', body: 'Every meal cooked to your family’s choice, from the welcome lunch to Sunday brunch.' },
  { icon: <MusicNoteIcon />, title: 'An evening of Baul', body: 'A local Baul artist performs by the bonfire, under the Birbhum sky.' },
  { icon: <ParkIcon />, title: 'A working farm', body: 'Fruit forest, vegetable garden, nursery, pond and ducks — and the bull, supervised.' },
  { icon: <NightsStayIcon />, title: 'Plant a memory', body: 'Each family plants a seedling, tagged with your name and the date.' },
];

const FarmStayPromo = () => {
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await client.graphql({ query: listActiveStayPackages });
        const items = res.data?.listStayPackages?.items || [];
        setPkg([...items].sort((a, b) => (a.displayOrder ?? 99) - (b.displayOrder ?? 99))[0] || null);
      } catch (e) {
        console.error('Error loading stay package for home page:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // nothing seeded yet: say nothing rather than show an empty band
  if (!loading && !pkg) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        py: { xs: 6, md: 10 },
        color: ink.onDark,
        backgroundColor: surface.darker,
        backgroundImage: pkg?.heroImage
          ? `linear-gradient(${alpha(brand.brownDark, 0.78)}, ${alpha(brand.brownDark, 0.88)}), url(${pkg.heroImage})`
          : `linear-gradient(135deg, ${brand.brownDark} 0%, ${brand.brown} 100%)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="overline" sx={{ letterSpacing: 4, color: brand.mustard, fontWeight: 700 }}>
              Stay with us
            </Typography>

            {loading ? (
              <>
                <Skeleton variant="text" sx={{ bgcolor: alpha(ink.onDark, 0.12), fontSize: '3rem', maxWidth: 420 }} />
                <Skeleton variant="text" sx={{ bgcolor: alpha(ink.onDark, 0.12), maxWidth: 320 }} />
              </>
            ) : (
              <>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    mt: 1,
                    color: ink.onDark,
                    fontSize: { xs: '2rem', md: '2.75rem' },
                    lineHeight: 1.15,
                    letterSpacing: -0.5,
                  }}
                >
                  {pkg.name}
                </Typography>
                {pkg.tagline && (
                  <Typography sx={{ mt: 1.5, color: ink.onDarkMuted, fontSize: { xs: 15, md: 17 }, lineHeight: 1.55 }}>
                    {pkg.tagline}
                  </Typography>
                )}
                <Typography sx={{ mt: 2.5, color: ink.onDarkMuted, lineHeight: 1.7, maxWidth: 520 }}>
                  {pkg.summary}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
                  <Chip
                    label={`${pkg.nights} nights · ${pkg.days} days`}
                    sx={{ bgcolor: alpha(ink.onDark, 0.16), color: ink.onDark, fontWeight: 500 }}
                  />
                  {pkg.price != null && (
                    <Chip
                      label={`${formatINR(pkg.price)} per person / night`}
                      sx={{ bgcolor: brand.mustard, color: ink.onMustard, fontWeight: 700 }}
                    />
                  )}
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
                  <Button
                    href="/farm-stay/book"
                    variant="contained"
                    size="large"
                    sx={{ ...MUSTARD_BUTTON_SX, px: 4 }}
                  >
                    Book your stay
                  </Button>
                  <Button
                    href="/farm-stay"
                    variant="outlined"
                    size="large"
                    sx={{
                      color: ink.onDark,
                      borderColor: alpha(ink.onDark, 0.55),
                      '&:hover': { borderColor: ink.onDark, bgcolor: alpha(ink.onDark, 0.08) },
                    }}
                  >
                    See the weekend
                  </Button>
                </Stack>
              </>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              {HIGHLIGHTS.map((h) => (
                <Grid item xs={12} sm={6} key={h.title}>
                  <Box
                    sx={{
                      p: 2.5, height: '100%', borderRadius: 3,
                      bgcolor: alpha(ink.onDark, 0.06),
                      border: `1px solid ${alpha(ink.onDark, 0.12)}`,
                      transition: 'background-color 160ms ease, border-color 160ms ease',
                      '&:hover': {
                        bgcolor: alpha(ink.onDark, 0.1),
                        borderColor: alpha(brand.mustard, 0.45),
                      },
                    }}
                  >
                    <Box sx={{ color: brand.mustard, mb: 1.25, display: 'flex' }}>{h.icon}</Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 15.5, color: ink.onDark, letterSpacing: -0.1 }}>
                      {h.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: ink.onDarkMuted, mt: 0.75, lineHeight: 1.6 }}>
                      {h.body}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default FarmStayPromo;
